<template>
	<div :class="'input-item ' + classList + ' ' + containerClasses" :style="styles" ref="input_container">
		<label class="input-label" :for="id" v-if="label !== ''"
			>{{ label }} <span class="asterisk" v-if="asterisk">*</span></label
		>
		<div class="input-field-block">
			<span class="input-append input-prefix" v-if="has_prefix">{{ prefix }}</span>
			<span
				v-show="clearButton && !hasFocus && value && !isLoading"
				@click.prevent="clear"
				style="
					z-index: 3;
					cursor: pointer;
					position: absolute;
					border-radius: 100%;
					bottom: 10px;
					right: 10px;
					height: 1em;
					width: 1em;
					text-align: center;
				"
			>
				<icon style="vertical-align: 0" type="close-s"></icon>
			</span>

			<!-- todo handle '' dateformats in backend -->

			<input
				type="text"
				@change="
					assumeDateFromTextInput();
					hideDatePicker($event);
				"
				@click="
					showDatepicker();
					focus();
				"
				@keypress.enter="
					assumeDateFromTextInput();
					hideDatePicker($event);
				"
				@blur="
					assumeDateFromTextInput();
					hideDatePicker($event);
				"
				@focus="
					showDatepicker();
					focus();
				"
				:id="id"
				:name="name"
				v-model="textInputDate"
				:placeholder="placeholder"
				:tabindex="tabindex"
				:required="required"
				ref="input"
				:autofocus="autofocus"
				:class="'input' + classList + ' ' + extraClasses"
				:disabled="disabled"
				autocomplete="off"
				aria-autocomplete="none"
			/>

			<datepicker
				@opened="focus"
				@closed="blur"
				@input="update($event)"
				:language="language_object"
				:monday-first="monday_first"
				:bootstrap-styling="false"
				input-class="hidden"
				ref-name="datepicker_input"
				ref="datepicker"
				:minimum-view="minimumView"
				:value="value"
				:disabled-picker="disabled"
				:required="required"
				:disabled-dates="disabledDates"
        :style="floatingBoxStyle"
			></datepicker>

			<span class="input-append input-suffix" v-if="has_suffix">{{ suffix }}</span>
		</div>
		<p class="input-help error" v-if="error" v-html="error"></p>
		<p class="input-help" v-if="help !== ''" v-html="help"></p>
	</div>
</template>

<script type="text/babel">
import en from "vuejs-datepicker/src/locale/translations/en";
import da from "vuejs-datepicker/src/locale/translations/da";
import no from "vuejs-datepicker/src/locale/translations/nb-NO";
import es from "vuejs-datepicker/src/locale/translations/es";
import sv from "vuejs-datepicker/src/locale/translations/sv";
import Datepicker from "vuejs-datepicker";
import Icon from "../UI/Icon";

export default {
	components: {
		datepicker: Datepicker,
		icon: Icon,
	},

	props: {
		id: { default: "" },
		name: { default: "" },
		value: {},
		placeholder: { default: "" },

		label: { default: "" },
		prefix: { default: "" },
		suffix: { default: "" },
		help: { default: "" },
		minimumView: { default: "day" },
		step: { default: null },

		asterisk: { default: false },
		error: { default: false },
		clearButton: { default: false },
		required: { default: false },
		disabled: { default: false },
		autofocus: { default: false },

		hasError: { default: false },
		hasSuccess: { default: false },
		isLoading: { default: false },
		isFull: { default: false },
		isModel: { default: true },
		onlyDate: { default: true },

		disabledDates: {
			type: Object,
			default() {
				return {};
			},
		},

		format: { default: false },

		language: { default: null },

		type: { default: "text" }, // default input types
		extraClasses: { default: "" },
		containerClasses: { default: "" },
		width: { default: null },
		tabindex: { default: null },
		size: { default: "regular" }, // regular, large
	},

	data: function () {
		return {
			generatedInputStyles: [],
			hasFocus: false,
			textInputDate: null,
			monday_first: true,
      floatingBoxPosition: {
        top: 0,
        left: 0,
      },
      dynamicPosition: false,
		};
	},

	watch: {
		value: function (value) {
			this.textInputDate = this.formatted_date;
		},
	},

	methods: {
    updateFloatingBoxPosition() {
      if (!this.dynamicPosition) return;
      const inputRect = this.$refs.input.getBoundingClientRect();
      this.floatingBoxPosition.top = inputRect.bottom;
      this.floatingBoxPosition.left = inputRect.left;
    },

		showDatepicker() {
			if (this.$refs.datepicker && !this.$refs.datepicker.isOpen) {
				this.$refs.datepicker.showCalendar();
			}
		},

		hideDatePicker(event) {
			this.$nextTick(() => {
				this.$refs.datepicker.close(false);
				this.blur();
			});
		},

		assumeDateFromTextInput() {
			if (this.textInputDate === "") {
				this.setValue("");
				return false;
			}

			if (this.minimumView !== "day") {
				return false;
			}

			let value = String(this.textInputDate).replace(new RegExp(/\D/g), "");

			let input_format = (window.user_account.settings.system.dates.format || "DDMMYYYY").replace(
				new RegExp(/\-/g),
				""
			);

			let day_offsets = [input_format.indexOf("DD"), input_format.indexOf("DD") + 1];
			let month_offsets = [input_format.indexOf("MM"), input_format.indexOf("MM") + 1];
			let year_offsets = [
				input_format.indexOf("YYYY"),
				input_format.indexOf("YYYY") + 1,
				input_format.indexOf("YYYY") + 2,
				input_format.indexOf("YYYY") + 3,
			];

			let regex_format = input_format
				.replace("DD", "([0-9]{2})")
				.replace("MM", "([0-9]{2})")
				.replace("YYYY", "([0-9]{2,4})?");

			if (!RegExp("^(" + regex_format + ")$", "g").test(value)) {
				this.setValue("");
				return false;
			}

			const current_year = String(new Date().getFullYear());

			let year = null;
			let month = null;
			let day = null;

			switch (value.length) {
				case 4:
					year = current_year;
					month = String(value[month_offsets[0]]) + String(value[month_offsets[1]]);
					day = String(value[day_offsets[0]]) + String(value[day_offsets[1]]);
					break;

				case 6:
					year =
						current_year[0] +
						current_year[1] +
						String(value[year_offsets[0]]) +
						String(value[year_offsets[1]]);
					month = String(value[month_offsets[0]]) + String(value[month_offsets[1]]);
					day = String(value[day_offsets[0]]) + String(value[day_offsets[1]]);
					break;

				case 8:
					year =
						String(value[year_offsets[0]]) +
						String(value[year_offsets[1]]) +
						String(value[year_offsets[2]]) +
						String(value[year_offsets[3]]);
					month = String(value[month_offsets[0]]) + String(value[month_offsets[1]]);
					day = String(value[day_offsets[0]]) + String(value[day_offsets[1]]);
					break;

				default:
					this.setValue("");
					return false;
			}

			let date = dayjs(`${year}-${month}-${day}`);

			if (!date.isValid()) {
				this.setValue("");
				return false;
			}

			this.setValue(date);
		},

		setValue(value) {
			if (this.format) {
				if (!dayjs.isDayjs(value)) {
					value = dayjs(value);
				}

				if (!value.isValid()) {
					this.emitInput(null);
					return false;
				}

				this.emitInput(value.format(this.format));
				return true;
			}

			if (dayjs.isDayjs(value)) {
				value = value.toDate();
			}

			this.emitInput(value);
			return true;
		},

		emitInput: debounce(function (newValue) {
			if (dayjs(newValue).isSame(dayjs(this.value))) {
				return false;
			}

			if (this.onlyDate && dayjs(this.value).isValid() && dayjs(newValue).isValid()) {
				if (
					dayjs(newValue).format(this.format || "YYYY-MM-DD") ===
					dayjs(this.value).format(this.format || "YYYY-MM-DD")
				) {
					return false;
				}
			}

			this.$emit("input", newValue);
		}, 100),

		update(e) {
			this.setValue(e);
		},

		focus() {
			this.hasFocus = true;
			this.$emit("focus");
		},

		blur() {
			this.hasFocus = false;
			this.$emit("blur");
		},

		clear() {
			this.$emit("input", "");
		},
	},

	computed: {
		language_object() {
			const languages = {
				en: en,
				no: no,
				es: es,
				da: da,
				sv: sv,
			};

			if (languages.hasOwnProperty(this.language)) {
				return languages[this.language];
			}

			if (languages.hasOwnProperty(window.app.locale)) {
				return languages[window.app.locale];
			}

			if (languages.hasOwnProperty(window.app.fallback_locale)) {
				return languages[window.app.fallback_locale];
			}

			return en;
		},

		formatted_date() {
			if (this.value !== "" && window.dayjs(this.value).isValid()) {
				let format = window.user_account.settings.system.dates.format || "DD-MM-YYYY";

				if (this.minimumView !== "day") {
					format = "YYYY-MM";
				}

				return window.dayjs(this.value).format(format);
			}

			return "";
		},

		styles() {
			let style = {};

			if (this.width !== null) {
				style["width"] = this.width;
			}

			return style;
		},

		inputStyles() {
			let style = {};

			this.generatedInputStyles.forEach(function (item) {
				style[item.type] = item.value;
			});

			return style;
		},

		has_suffix() {
			return this.suffix && this.suffix !== "";
		},

		has_prefix() {
			return this.prefix && this.prefix !== "";
		},

		classList() {
			let classList = "";

			classList += " size-" + this.size;
			classList += " type-" + this.type;

			if (this.has_suffix) {
				classList += " has-suffix";
			}

			if (this.has_prefix) {
				classList += " has-prefix";
			}

			if (this.label !== "") {
				classList += " has-label";
			}

			if (this.help !== "") {
				classList += " has-help";
			}

			if (this.hasSuccess) {
				classList += " has-success";
			}

			if (this.hasError || this.error) {
				classList += " has-error";
			}

			if (this.isLoading) {
				classList += " is-loading";
			}

			if (this.isFull) {
				classList += " is-full";
			}

			if (this.required) {
				classList += " is-required";
			}

			if (this.hasFocus) {
				classList += " has-focus";
			}

			return classList;
		},

    floatingBoxStyle() {
      if (this.dynamicPosition) {
        return {
          position: 'fixed',
          top: `${this.floatingBoxPosition.top}px`,
          left: `${this.floatingBoxPosition.left}px`,
          zIndex: '100',
        };
      }
      return {};
    },
	},

	mounted() {
		if (this.has_prefix) {
			const currentPaddingLeft =
				getComputedStyle(this.$el.querySelector(".input")).getPropertyValue("padding-left").replace("px", "") *
				1;
			this.generatedInputStyles.push({
				type: "padding-left",
				value: this.$el.querySelector(".input-prefix").clientWidth + currentPaddingLeft + "px",
			});
		}

		if (this.has_suffix) {
			const currentPaddingRight =
				getComputedStyle(this.$el.querySelector(".input")).getPropertyValue("padding-right").replace("px", "") *
				1;
			this.generatedInputStyles.push({
				type: "padding-right",
				value: this.$el.querySelector(".input-suffix").clientWidth + currentPaddingRight + "px",
			});
		}

		this.textInputDate = this.formatted_date;
		this.monday_first = window.user_account.settings.system.dates.monday_first;

    Vue.nextTick(() => {
      this.updateFloatingBoxPosition();
    });
    window.addEventListener('resize', this.updateFloatingBoxPosition);
    window.addEventListener('scroll', this.updateFloatingBoxPosition);

    /* Only enables the dynamic positioning if the component is inside a CollapseBox */
    let parent = this.$parent;
    while (parent) {
      if (parent.$options.name === 'CollapseBox' && parent.$refs.collapseBox) {
        this.dynamicPosition = true;
        break;
      }
      parent = parent.$parent;
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateFloatingBoxPosition);
  },
};
</script>
